import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import _ from 'lodash';
import cn from 'classnames';
import './ChatFilters.scss';

import { getLookbooks, isBrand, getBrandLists } from '../../Helpers/user_helpers';
import { getBrandRequests, sortBrandListsByRecency } from '../../Helpers/brand_helpers';

const ChatFilters = props => {
  /*
    Currently only a brand feature.
  */
  const { user, allChats, analytics, activeFilterObjects } = props;
  const userIdsInChat = new Set(allChats.map(chat => chat.User_id));
  const newChats = allChats.filter(chat => chat.hasNewMessagesForBrand && !chat.isDismissedByBrand);
  const userIdsWithNewChats = new Set(newChats.map(chat => chat.User_id));

  // Allow Filtering by Lists
  const lists = isBrand(user) ? sortBrandListsByRecency(getBrandLists(user)) : [];
  const getFilterForList = list => {
    const listUserIdsSet = new Set(list.users.map(u => u.User_id));
    const listUsersWithChats = list.users.filter(u => userIdsInChat.has(u.User_id));
    const newCount = listUsersWithChats.filter(u => userIdsWithNewChats.has(u.User_id)).length;
    return {
      key: `list-${list.id}`,
      display: list.title,
      count: listUsersWithChats.length,
      newCount,
      filter: chat => listUserIdsSet.has(chat.User_id)
    };
  };

  // Allow filtering by Lookbooks
  const requests = getBrandRequests(analytics);
  const requestsByLookbook = _.groupBy(requests, 'Lookbook_id');
  const lookbooks = _.orderBy(
    getLookbooks(user).filter(lookbook => !!requestsByLookbook[lookbook.id]),
    'createdAt',
    'desc'
  );
  const getFilterForLookbook = lookbook => {
    const lookbookRequests = requestsByLookbook[lookbook.id] || [];
    const requestUserIdSet = new Set(lookbookRequests.map(r => r.User_id));
    const newCount = lookbookRequests.filter(r => userIdsWithNewChats.has(r.User_id)).length;
    return {
      key: `lookbook-${lookbook.id}`,
      display: lookbook.title,
      count: lookbookRequests.length,
      newCount,
      filter: chat => {
        return requestUserIdSet.has(chat.User_id);
      }
    };
  };

  // Allow filtering by Gifting Status
  const acceptedGiftingUserIds = new Set(requests.filter(r => r.userAccepted && r.brandAccepted).map(r => r.User_id));
  const awaitingGiftingResponseUserIds = new Set(requests.filter(r => r.brandAccepted && !r.userAccepted && !r.userRejected).map(r => r.User_id));
  const giftingStatusFilters = [
    {
      key: 'gifting-accepted',
      display: 'Accepted Gifting',
      count: allChats.filter(chat => acceptedGiftingUserIds.has(chat.User_id)).length,
      newCount: newChats.filter(chat => acceptedGiftingUserIds.has(chat.User_id)).length,
      filter: chat => {
        return acceptedGiftingUserIds.has(chat.User_id);
      }
    },
    {
      key: 'gifting-awaiting-response',
      display: 'Awaiting Gifting Response',
      count: allChats.filter(chat => awaitingGiftingResponseUserIds.has(chat.User_id)).length,
      newCount: newChats.filter(chat => awaitingGiftingResponseUserIds.has(chat.User_id)).length,
      filter: chat => {
        return awaitingGiftingResponseUserIds.has(chat.User_id);
      }
    }
  ];
  const getFilterForGiftingStatus = giftingStatus => giftingStatusFilters.find(g => g.key === giftingStatus.key);

  // Geo Filters
  const geoFilters = [
    {
      key: 'geo-US',
      display: 'United States',

      count: allChats.filter(chat => chat.user.countryCode === 'US').length,
      newCount: newChats.filter(chat => chat.user.countryCode === 'US').length,
      filter: chat => chat.user.countryCode === 'US'
    }
  ];
  const getFilterForGeo = geoFilter => geoFilters.find(g => g.key === geoFilter.key);

  // Other filters
  const otherFilters = [
    {
      key: 'other-dismissed',
      display: 'Dismissed Chats',
      count: allChats.filter(chat => chat.isDismissedByBrand).length,
      newCount: 0,
      filter: chat => chat.isDismissedByBrand
    }
  ];

  const getSection = ({ title, objects, keyPrefix, getFilter }) => {
    const validObjects = objects.filter(object => !!getFilter(object).count);
    if (!validObjects.length) return null;
    return (
      <div className='filter-section'>
        <div className='filter-section-header'>{title}</div>
        <div className='filters'>
          {validObjects.map(object => {
            const filter = getFilter(object);
            const active = props.isFilterActive(filter);
            const applyFilter = () => {
              if (active) return props.setFilters(activeFilterObjects.filter(f => f.key !== filter.key));
              const newSetOfFilters = [...activeFilterObjects, filter].filter(f => {
                if (f.key === filter.key) return true;
                if (f.key.includes(keyPrefix)) return false; // Hide all other list filters
                return true;
              });
              props.setFilters(newSetOfFilters);

              // If we are filtering after pressing a button with "New" chats, we should turn on the "New Only" filter
              // filter.newCount && !activeFilterObjects.length && props.turnOnNewOnlyFilter(); // Currently disabled

              // If we are using dual filters, we should turn off the "New Only" filter
              // if (newSetOfFilters.length > 1) props.turnOffNewOnlyFilter(); // Currently disabled

              props.closePanel();
            };

            return (
              <div title={filter.display} className={cn('filter', { active })} onClick={applyFilter} key={filter.display}>
                <div className='title' onClick={applyFilter}>
                  {filter.display}
                </div>
                {filter.newCount ? <div className='count new'>{filter.newCount} NEW</div> : <div className='count'>{filter.count}</div>}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className='chat-filters-outer-container'>
      <div className='panel-header'>
        <div className='title'>Filters</div>
        <div onClick={props.closePanel} className='close-btn'>
          Done
        </div>
      </div>
      <div className='chat-filters-inner-container'>
        <div className='filter-sections'>
          {/* Gifting Status */}
          {getSection({
            title: 'Gifting Status',
            objects: giftingStatusFilters,
            keyPrefix: 'gifting-',
            getFilter: getFilterForGiftingStatus
          })}
          {/* Lists */}
          {getSection({ title: 'Lists', objects: lists, keyPrefix: 'list-', getFilter: getFilterForList })}
          {/* Lookbooks */}
          {getSection({
            title: 'Lookbooks',
            objects: lookbooks,
            keyPrefix: 'lookbook-',
            getFilter: getFilterForLookbook
          })}
          {/* Geo Filters */}
          {getSection({ title: 'Geo', objects: geoFilters, keyPrefix: 'geo-', getFilter: getFilterForGeo })}
          {/* Other Filters */}
          {getSection({ title: 'Other', objects: otherFilters, keyPrefix: 'other-', getFilter: f => f })}
        </div>
      </div>
    </div>
  );
};

ChatFilters.propTypes = {
  // From outsid
  allChats: PropTypes.array.isRequired,
  isFilterActive: PropTypes.func.isRequired,
  activeFilterObjects: PropTypes.array.isRequired,
  setFilters: PropTypes.func.isRequired,
  turnOnNewOnlyFilter: PropTypes.func.isRequired,
  turnOffNewOnlyFilter: PropTypes.func.isRequired,
  closePanel: PropTypes.func.isRequired,

  // From inside
  user: PropTypes.object.isRequired,
  analytics: PropTypes.object.isRequired
};

const mapStateToProps = state => {
  const { user, analytics } = state;
  return { user, analytics };
};

export default connect(mapStateToProps, {
  // functions go here
})(ChatFilters);
